"use client";

import React from "react";

type PassState = {
  status: string;
  name: string;
  role: string;
  tags: string[];
  followers: string;
  passId: string;
};

const PASS_STATES: PassState[] = [
  {
    status: "Applicant",
    name: "[Your Name Here]",
    role: "Add your platform",
    tags: [],
    followers: "—",
    passId: "KD-••••••",
  },
  {
    status: "In Review",
    name: "Alex Tan",
    role: "TikTok · Instagram",
    tags: ["Beauty", "Lifestyle"],
    followers: "10K–25K",
    passId: "KD-4X8•••",
  },
  {
    status: "Matched",
    name: "Alex Tan",
    role: "TikTok · Instagram",
    tags: ["Beauty", "Lifestyle", "UGC"],
    followers: "10K–25K",
    passId: "KD-4X82K1",
  },
  {
    status: "Active",
    name: "Alex Tan",
    role: "TikTok · Instagram",
    tags: ["Beauty", "Lifestyle", "UGC"],
    followers: "10K–25K",
    passId: "KD-4X82K1",
  },
];

type CreatorPassPreviewProps = {
  index: number;
};

export default function CreatorPassPreview({ index }: CreatorPassPreviewProps) {
  const state = PASS_STATES[index % PASS_STATES.length];

  return (
    <div className="pass-stage relative flex items-center justify-center min-h-[340px] w-full">
      <div className="pass">
        <div className="pass-top">
          <div className="pass-brand">
            Kingdom Digital
            <b>Creator Pass</b>
          </div>
          <div className="pass-hex">KD</div>
        </div>

        <div className="pass-name">{state.name}</div>
        <div className="pass-role">{state.role}</div>

        <div className="pass-tags">
          {state.tags.length > 0 ? (
            state.tags.map((tag) => (
              <span key={tag} className="pass-tag">
                {tag}
              </span>
            ))
          ) : (
            <span className="pass-tag pass-tag--muted">Choose your category</span>
          )}
        </div>

        <div className="pass-bottom">
          <div className="pass-follow">
            FOLLOWERS
            <br />
            <b>{state.followers}</b>
          </div>
          <div className="pass-status">{state.status}</div>
        </div>

        <div className="pass-id">{state.passId}</div>

        <div className="scan-beam" aria-hidden="true" />
        <div className="match-badge" aria-hidden="true">
          <span className="match-badge-pill">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 13l4 4L19 7"
                stroke="#150F2E"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Match Found
          </span>
        </div>
      </div>

      <style jsx>{`
        .pass {
          width: 400px;
          max-width: 92vw;
          min-height: 265px;
          border-radius: 24px;
          background: linear-gradient(155deg, #3b2c7a 0%, #23184f 55%, #150f2e 100%);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 30px 60px -20px rgba(21, 15, 46, 0.4);
          padding: 26px 28px;
          position: relative;
          color: #f5f3fa;
          overflow: hidden;
          transform: rotate(6deg);
          animation:
            passIn 1s cubic-bezier(0.16, 1, 0.3, 1) both,
            floatCard 6s ease-in-out 1s infinite;
        }

        @keyframes passIn {
          from {
            opacity: 0;
            transform: rotate(6deg) translateY(30px) scale(0.94);
          }
          to {
            opacity: 1;
            transform: rotate(6deg) translateY(0) scale(1);
          }
        }

        @keyframes floatCard {
          0%,
          100% {
            transform: rotate(6deg) translateY(0);
          }
          50% {
            transform: rotate(4deg) translateY(-8px);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .pass {
            animation: none;
          }
        }

        @media (max-width: 767px) {
          .pass-stage {
            min-height: 260px;
          }
          .pass {
            min-height: 200px;
            padding: 20px 22px;
          }
          .pass-hex {
            width: 34px;
            height: 34px;
            font-size: 12px;
          }
          .pass-brand b {
            font-size: 15px;
          }
          .pass-name {
            font-size: 22px;
            margin-top: 14px;
          }
          .pass-role {
            font-size: 13px;
          }
          .pass-tags {
            margin-top: 12px;
          }
          .pass-tag {
            padding: 4px 9px;
          }
          .pass-bottom {
            margin-top: 14px;
          }
          .pass-follow b {
            font-size: 14px;
          }
          .pass-status {
            padding: 4px 9px;
          }
          .pass-id {
            bottom: 12px;
            right: 20px;
          }
        }

        .pass::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(
            115deg,
            transparent 38%,
            rgba(139, 92, 246, 0.22) 50%,
            rgba(62, 111, 245, 0.14) 60%,
            transparent 72%
          );
          pointer-events: none;
        }

        .pass-top {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          position: relative;
          z-index: 2;
        }

        .pass-brand {
          font-family: var(--font-mono, ui-monospace, monospace);
          font-size: 12px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(245, 243, 250, 0.72);
        }

        .pass-brand b {
          display: block;
          font-size: 17px;
          color: #f5f3fa;
          letter-spacing: 0;
          text-transform: none;
          margin-top: 2px;
          font-weight: 700;
        }

        .pass-hex {
          width: 40px;
          height: 40px;
          background: #8b5cf6;
          color: #150f2e;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-mono, ui-monospace, monospace);
          font-weight: 700;
          font-size: 14px;
          clip-path: polygon(25% 6%, 75% 6%, 100% 50%, 75% 94%, 25% 94%, 0% 50%);
          flex-shrink: 0;
        }

        .pass-name {
          font-size: 28px;
          font-weight: 700;
          margin-top: 18px;
          position: relative;
          z-index: 2;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .pass-role {
          font-size: 14px;
          color: rgba(245, 243, 250, 0.72);
          margin-top: 4px;
          position: relative;
          z-index: 2;
        }

        .pass-tags {
          display: flex;
          gap: 7px;
          margin-top: 16px;
          flex-wrap: wrap;
          position: relative;
          z-index: 2;
        }

        .pass-tag {
          font-family: var(--font-mono, ui-monospace, monospace);
          font-size: 11px;
          padding: 5px 11px;
          border-radius: 100px;
          border: 1px solid rgba(255, 255, 255, 0.18);
          color: rgba(245, 243, 250, 0.72);
        }

        .pass-tag--muted {
          color: rgba(245, 243, 250, 0.4);
          border-style: dashed;
        }

        .pass-bottom {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-top: 20px;
          position: relative;
          z-index: 2;
        }

        .pass-follow {
          font-family: var(--font-mono, ui-monospace, monospace);
          font-size: 12px;
          color: rgba(245, 243, 250, 0.45);
        }

        .pass-follow b {
          color: #8b5cf6;
          font-size: 15px;
        }

        .pass-status {
          font-family: var(--font-mono, ui-monospace, monospace);
          font-size: 11px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          padding: 5px 10px;
          border-radius: 6px;
          background: rgba(139, 92, 246, 0.2);
          color: #c4b5fd;
          margin-bottom: 12px;
        }

        .pass-id {
          position: absolute;
          bottom: 16px;
          right: 28px;
          font-family: var(--font-mono, ui-monospace, monospace);
          font-size: 10px;
          color: rgba(245, 243, 250, 0.45);
          letter-spacing: 0.06em;
          z-index: 2;
        }

        .scan-beam {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, #37ded7, transparent);
          box-shadow: 0 0 16px 3px rgba(55, 222, 215, 0.85);
          opacity: 0;
          z-index: 5;
          pointer-events: none;
        }

        :global(.pass.scanning) .scan-beam {
          animation: scanSweep 1.1s ease-in-out 1;
        }

        @keyframes scanSweep {
          0% {
            top: 0%;
            opacity: 0;
          }
          8% {
            opacity: 1;
          }
          92% {
            opacity: 1;
          }
          100% {
            top: 100%;
            opacity: 0;
          }
        }

        .match-badge {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 6;
          opacity: 0;
          pointer-events: none;
        }

        :global(.pass.scanning) .match-badge {
          /* fill-mode "both" (not just "forwards") is required here: the base
             rule above sets opacity:0, so without the "backwards" half the
             badge would stay invisible for the whole delay and only flash
             into view right as it starts fading out. */
          animation: matchFade 0.5s ease 7s both;
        }

        .match-badge-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #37ded7;
          color: #150f2e;
          font-family: var(--font-mono, ui-monospace, monospace);
          font-weight: 700;
          font-size: 14px;
          letter-spacing: 0.02em;
          padding: 10px 18px;
          border-radius: 100px;
          box-shadow: 0 10px 30px -6px rgba(55, 222, 215, 0.7);
          transform: scale(0.6);
          opacity: 0;
        }

        :global(.pass.scanning) .match-badge-pill {
          animation: matchPop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        }

        @keyframes matchPop {
          from {
            opacity: 0;
            transform: scale(0.6);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes matchFade {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          :global(.pass.scanning) .scan-beam,
          :global(.pass.scanning) .match-badge,
          :global(.pass.scanning) .match-badge-pill {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
